import { memo, useState } from 'react';
import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';

import DateSelectBox from './DateSelectBox';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';

const IncomeOutcomeStatistics = () => {
  const axiosPrivate = useAxiosPrivate();
  const [searchParams] = useSearchParams();
  const [hasError, setHasError] = useState(false);
  const [startAndFinishDate, setStartAndFinishDate] = useState({
    startDate: '',
    finishDate: '',
  });

  const accountId = searchParams.get('accountId');

  const search = `${
    startAndFinishDate.startDate ? '?from=' + startAndFinishDate.startDate : ''
  }${
    startAndFinishDate.finishDate
      ? '&till=' + startAndFinishDate.finishDate
      : ''
  }`;
  const { isLoading, isFetching, isError, data, error } = useQuery({
    queryKey: [
      'analytic/account/statisticByTypes/{accountId}',
      startAndFinishDate,
      accountId,
    ],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `analytic/account/statisticByTypes/${accountId}${search}`
      );
      return response.data.data;
    },

    enabled: !!accountId && !hasError,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  return (
    <div className='residential-complex-item income-by-residential-complex !shadow-md !base-border'>
      <div className='residential-complex-item-header'>
        <div className='residential-complex-item-title font-semibold text-lg income-by-residential-complex-title'>
          Kassa kirim-chiqimlari bo'yicha ma'lumot
        </div>
        <div className='w-[150px]'>
          <DateSelectBox
            setStartAndFinishDate={setStartAndFinishDate}
            disabled={isLoading || isFetching}
          />
        </div>
      </div>

      <div className='changes-table'>
        <TableContainer className='flex-auto w-full h-full !shadow-none !base-border'>
          <Table
            stickyHeader
            sx={{ width: 'inherit' }}
            aria-labelledby='tableTitle'
          >
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} className='!border-r'>
                  №
                </TableCell>
                <TableCell rowSpan={2} className='!border-r'>
                  Nomi
                </TableCell>
                <TableCell align='center' className='!border-r' colSpan={2}>
                  Kirim
                </TableCell>
                <TableCell align='center' className='!border-r' colSpan={2}>
                  Chiqim
                </TableCell>
                <TableCell align='center' className='!border-r' colSpan={2}>
                  Balans
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='center' className='!border-r'>
                  USD ($)
                </TableCell>
                <TableCell align='center' className='!border-r'>
                  UZS
                </TableCell>
                <TableCell align='center' className='!border-r'>
                  USD ($)
                </TableCell>
                <TableCell align='center' className='!border-r'>
                  UZS
                </TableCell>
                <TableCell align='center' className='!border-r'>
                  USD ($)
                </TableCell>
                <TableCell align='center' className='!border-r'>
                  UZS
                </TableCell>
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableBody className='overflow-hidden'>
                <TableRow>
                  <TableCell colSpan={8}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : isError ? (
              <TableBody className='overflow-hidden'>
                <TableRow>
                  <TableCell colSpan={8}>
                    <div className='flex flex-col items-center'>
                      {error?.response?.data?.message && (
                        <span className='text-red-600 font-medium'>
                          {error?.response?.data?.message}
                        </span>
                      )}
                      {error?.response?.data?.details &&
                        error?.response?.data?.details[0]?.message && (
                          <div>
                            <span className='text-red-600 font-medium'>
                              Xalotik xabari:
                            </span>
                            <span>
                              {error?.response?.data?.details[0]?.message}
                            </span>
                          </div>
                        )}
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : data && data?.length > 0 ? (
              <TableBody className='overflow-hidden'>
                {data.map((row, rowIndex) => (
                  <TableRow hover tabIndex={-1} key={'row-' + rowIndex}>
                    <TableCell>{rowIndex + 1}</TableCell>
                    <TableCell>{row?.name || ''}</TableCell>
                    <TableCell>
                      <CurrencyFormat
                        value={row?.income_val || '0'}
                        suffix=' $'
                      />
                    </TableCell>
                    <TableCell>
                      <CurrencyFormat
                        value={row?.income_sum || '0'}
                        suffix=' UZS'
                      />
                    </TableCell>
                    <TableCell>
                      <CurrencyFormat
                        value={row?.expense_val || '0'}
                        suffix=' $'
                      />
                    </TableCell>
                    <TableCell>
                      <CurrencyFormat
                        value={row?.expense_sum || '0'}
                        suffix=' UZS'
                      />
                    </TableCell>
                    <TableCell>
                      <CurrencyFormat
                        value={row?.income_val - row?.expense_val}
                        suffix=' $'
                        allowNegative={true}
                      />
                    </TableCell>
                    <TableCell>
                      <CurrencyFormat
                        value={row?.income_sum - row?.expense_sum}
                        suffix=' UZS'
                        allowNegative={true}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody component='div'>
                <TableRow component='div'>
                  <TableCell colSpan={6}>
                    <span className='text-gray-400 flex items-center justify-center'>
                      <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                      Ma'lumot topilmadi!
                    </span>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default memo(IncomeOutcomeStatistics);
